/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {FindNearbyRequestArgs} from '@ion/core/src/services/ArcGISUtils';
import {CVSAssetType, FindNearbyType} from '@ion/core/src/types/entities';
import {convertOdometer, isMetric} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import {navigateToGoogleDirections} from '@ion/dls/src/maps/helpers/globeUtils';
import {setStreetViewAsset} from '@/components/streetview/store';
import {OnFindNearByProps} from '@/routes/tracking//FindNearByPopupContent';
import useViewAssetHistory from '@/routes/tracking/hooks/history/useViewAssetHistory';
import {CtaType} from '@/routes/tracking/SelectedAssetPopup';
import {
  findNearby,
  setAssetDetailView,
  setFindButtonClickStatus,
  setFindNearbyPopup
} from '@/store/actions/tracking';

/**
 * The usePopupActions hook is used for the popup's CTA Actions.
 * 
 * ```typescript
 * 
 *   import {usePopupActions} from './hooks/usePopupActions';
 * 
 *   const {
 *     onSelectedAssetCtaClick,
 *     onFindNearBySubmit,
 *     showSidesheetAssetDetail
 *   } = usePopupActions();
 * 
 *   <Component onCtaClick={onSelectedAssetCtaClick}
 *     onFindNearBySubmit={onFindNearBySubmit}
 *     onPopupClick={showSidesheetAssetDetail} />
 * 
 * ```
 */
export const usePopupActions = () => {
  const dispatch                = useDispatch();
  const isDesktop               = useIsDesktop();
  const onAssetViewHistoryClick = useViewAssetHistory();

  const onSelectedAssetCtaClick = useCallback(function(type: CtaType, assetData: any) {
    if (type === CtaType.HISTORY) {
      onAssetViewHistoryClick(assetData);
    }
    if (type === CtaType.NAVIGATION) {
      navigateToGoogleDirections(assetData);
    }
    if (type === CtaType.STREETVIEW) {
      setStreetViewAsset(assetData);
    }
  }, [onAssetViewHistoryClick, navigateToGoogleDirections]);

  const onFindNearBySubmit = useCallback(function({
    findNearbyChip,
    uom,
    distance,
    label,
    asset: selectedAsset
  }: OnFindNearByProps) {
    findNearbyChip.map((chip) => {
      if (chip?.isSelected) {
        const dist = isMetric(uom) ? convertOdometer(distance, uom, Unit.US, true) : distance;
        let {latitude, longitude} = selectedAsset;
        if (!latitude && !longitude) {
          const {geometry} = selectedAsset || {};
          if (geometry) {
            const {type, centroid} = geometry;
            if (type === 'polygon') {
              latitude  = centroid.latitude;
              longitude = centroid.longitude;
            }
          }
        }
        const params: FindNearbyRequestArgs = {
          latitude,
          longitude,
          miles : dist,
          label : label
        };
        dispatch(findNearby({
          ...params,
          findType : FindNearbyType.geofence
        }) as any);
        dispatch(setFindButtonClickStatus(true));
      }
      if (isDesktop) dispatch(setFindNearbyPopup(false));
    });
  }, []);

  const showSidesheetAssetDetail = useCallback(function(
    canShowSidesheet: boolean,
    asset: CVSAssetType,
    isMapSelection: boolean
  ) {
    canShowSidesheet && dispatch(setAssetDetailView({
      value            : true,
      forcePanelToOpen : true,
      fromTools        : true,
      selectedItem     : asset,
      isMapSelection
    }) as any);
  }, []);
  
  return {
    onSelectedAssetCtaClick,
    onFindNearBySubmit,
    showSidesheetAssetDetail
  };
};
  
export default usePopupActions;
